import { usePage } from "@/components/page/PageContext";
import {
  Card,
  MultiSeriesChart,
  PercentageChart,
  SingleSeriesChart,
} from "@/pages/data/DataChart";
import { getAggregateData, ZoneQuery } from "@/queries/report";
import { useReportContext } from "@/stores/ReportContext";
import { useEffect } from "react";

function TotalPaxCount() {
  const { reportId } = useReportContext();
  const {
    data,
    isLoading: _isLoading,
    isSuccess,
  } = getAggregateData(
    [
      {
        query: {
          measure: "pax",
          type: "terminal",
        },
      },
      {
        query: {
          measure: "flight",
          type: "terminal",
        },
      },
      {
        query: {
          measure: "bag",
          type: "terminal",
          pax_type: "direct",
        },
      },
    ],
    reportId
  );

  if (!isSuccess) {
    return null;
  }
  const [paxData, flightData, bagData] = data;
  return (
    <div className="flex flex-col space-y-4">
      <Card title="Total Departing & Arriving Passenger Count">
        <SingleSeriesChart
          type="column"
          values={paxData.values}
          labels={paxData.labels}
          xAxisTitle="Time"
          yAxisTitle="Passengers"
          colorIndex={0}
        />
      </Card>
      <Card title="Total Flight Count">
        <SingleSeriesChart
          type="column"
          values={flightData.values}
          labels={flightData.labels}
          xAxisTitle="Time"
          yAxisTitle="Flights"
          colorIndex={1}
        />
      </Card>
      <Card title="Total Baggage Count">
        <SingleSeriesChart
          type="column"
          values={bagData.values}
          labels={bagData.labels}
          xAxisTitle="Time"
          yAxisTitle="Bags"
          colorIndex={2}
        />
      </Card>
    </div>
  );
}

function ArrVsDeparting() {
  const { reportId } = useReportContext();

  const {
    data,
    isLoading: _isLoading,
    isSuccess,
  } = getAggregateData(
    [
      {
        query: {
          measure: "pax",
          direction: "arr",
          type: "terminal",
        },
      },
      {
        query: {
          measure: "pax",
          type: "terminal",
        },
      },
    ],
    reportId
  );

  if (!isSuccess) {
    return null;
  }

  const [arrData, totalData] = data;
  return (
    <Card title="Daily Pax Traffic - Arriving v/s Departing">
      <PercentageChart
        value={arrData.sum}
        total={totalData.sum}
        valueText={"Arriving"}
        remainText={"Departing"}
      />
    </Card>
  );
}

function HourlyDistribution() {
  const { reportId } = useReportContext();

  const {
    data,
    isLoading: _isLoading,
    isSuccess,
  } = getAggregateData(
    [
      {
        query: {
          measure: "pax",
          direction: "arr",
          type: "terminal",
        },
      },
      {
        query: {
          measure: "pax",
          direction: "dep",
          type: "terminal",
        },
      },
      {
        query: {
          measure: "pax",
          type: "terminal",
        },
      },
    ],
    reportId
  );

  if (!isSuccess) {
    return null;
  }

  const [arrData, depData, totalData] = data;
  return (
    <Card title="Daily Pax Traffic - Arriving v/s Departing">
      <MultiSeriesChart
        chartType="line"
        series={[
          {
            name: "Arriving",
            values: arrData.values,
          },
          {
            name: "Departing",
            values: depData.values,
          },
          {
            name: "Total",
            values: totalData.values,
          },
        ]}
        labels={arrData.labels}
        yAxisTitle={"Passengers"}
        xAxisTitle={"Time"}
      />
    </Card>
  );
}

function PaxByLocationType() {
  const { reportId } = useReportContext();

  const gatesQuery: ZoneQuery = {
    measure: "pax",
    type: "gate",
  };

  const checkpointsQuery: ZoneQuery = {
    measure: "pax",
    type: "checkpoint",
  };

  const ticketingsQuery: ZoneQuery = {
    measure: "pax",
    type: "ticketing",
  };

  const doorsQuery: ZoneQuery = {
    measure: "pax",
    type: "door",
  };

  const {
    data,
    isLoading: _isLoading,
    isSuccess,
  } = getAggregateData(
    [
      {
        query: gatesQuery,
      },
      {
        query: checkpointsQuery,
      },
      {
        query: ticketingsQuery,
      },
      {
        query: doorsQuery,
      },
    ],
    reportId
  );

  if (!isSuccess) {
    return null;
  }

  const [gatesData, checkpointsData, ticketingsData, doorsData] = data;

  return (
    <div className="p-2">
      <Card title="Passenger Volume by Location Type">
        <div className="text-sm text-slate-400 mb-3">
          Hourly passenger volume from stored zone aggregates for each location.
          Values are the same figures returned by /aggregate (not rescaled to
          terminal total). Passengers appear at multiple locations as they move,
          so series are compared side-by-side rather than stacked to one total.
        </div>
        <MultiSeriesChart
          chartType="column"
          series={[
            {
              name: "Gate Lounges",
              values: gatesData.values,
            },
            {
              name: "Checkpoints",
              values: checkpointsData.values,
            },
            {
              name: "Ticket Counters",
              values: ticketingsData.values,
            },
            {
              name: "Doors",
              values: doorsData.values,
            },
          ]}
          labels={gatesData.labels}
          xAxisTitle="Time"
          yAxisTitle="Passengers"
          stacked={false}
        />
      </Card>
    </div>
  );
}

export default function PaxOverviewPage() {
  const { setTitles } = usePage();
  useEffect(() => {
    setTitles(["Passenger Overview"]);
  }, []);
  return (
    <div>
      <div className="mt-4">
        <PaxByLocationType />
      </div>
      <TotalPaxCount />
      <div className="w-full flex flex-col md:flex-row h-auto md:h-90 gap-4 mt-4">
        <div className="w-full md:w-2/5 h-auto md:h-full">
          <ArrVsDeparting />
        </div>
        <div className="w-full md:w-3/5 h-auto md:h-full">
          <HourlyDistribution />
        </div>
      </div>
    </div>
  );
}
